export const StatusPill = ({ status }) => {
  const s = (status || "pending").toLowerCase();
  return <span className={`pill ${s.replace(/\s+/g, "-")} whitespace-nowrap`} data-testid={`status-${s.replace(/\s+/g, "-")}`}>{status}</span>;
};

export const SectionTitle = ({ title, subtitle, action }) => (
  <div className="flex items-end justify-between mb-4 gap-3">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{title}</h1>
      {subtitle && <p className="text-sm text-muted mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, accent = "purple", trend }) => {
  const colorMap = {
    purple: "from-[#8B5CF6]/30 to-[#EC4899]/10",
    cyan:   "from-[#22D3EE]/30 to-[#8B5CF6]/10",
    lime:   "from-[#A3E635]/30 to-[#22D3EE]/10",
    pink:   "from-[#EC4899]/30 to-[#F59E0B]/10",
    amber:  "from-[#F59E0B]/30 to-[#EC4899]/10",
    green:  "from-[#10B981]/30 to-[#A3E635]/10",
  };
  const iconColor = {
    purple: "#8B5CF6", cyan: "#22D3EE", lime: "#A3E635",
    pink: "#EC4899", amber: "#F59E0B", green: "#10B981",
  };
  return (
    <div className="glass p-5 hover-lift relative overflow-hidden" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl bg-gradient-to-br ${colorMap[accent]}`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted font-bold">{label}</span>
          {Icon && (
            <div className="w-9 h-9 rounded-xl grid place-items-center" style={{background: `${iconColor[accent]}1a`, color: iconColor[accent]}}>
              <Icon size={18} weight="bold" />
            </div>
          )}
        </div>
        <div className="text-3xl font-black tracking-tight text-white">{value}</div>
        {trend && <div className="text-xs text-muted mt-1">{trend}</div>}
      </div>
    </div>
  );
};

export const EmptyState = ({ title = "Nothing here yet", subtitle = "Once you start, it'll show up here.", icon: Icon }) => (
  <div className="glass p-10 text-center">
    {Icon && <div className="w-14 h-14 mx-auto rounded-2xl grid place-items-center bg-white/5 mb-4 text-[#8B5CF6]"><Icon size={24} weight="bold" /></div>}
    <div className="text-white font-bold">{title}</div>
    <div className="text-muted text-sm mt-1">{subtitle}</div>
  </div>
);

export const Avatar = ({ src, name = "", size = 40 }) => {
  const initials = name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
  return src ? (
    <img src={src} alt={name} style={{ width: size, height: size }} className="rounded-full object-cover border border-white/10" />
  ) : (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center text-white font-bold text-sm">{initials || "?"}</div>
  );
};

export const ProgressBar = ({ value = 0, max = 100, color = "#8B5CF6" }) => {
  const pct = Math.min(100, (value / Math.max(1, max)) * 100);
  return (
    <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full" style={{
        width: `${pct}%`,
        background: `linear-gradient(90deg, ${color}, ${color}99)`,
        boxShadow: `0 0 10px ${color}88`
      }} />
    </div>
  );
};

/**
 * ShowMore: simple Show-More / Load-More button for client-side pagination.
 * Use with a useState page-size counter (e.g. const [n,setN]=useState(10); items.slice(0,n)).
 */
export const ShowMore = ({ visible, total, onMore, step = 10, testid }) => {
  if (total <= visible) return null;
  return (
    <button
      onClick={() => onMore(Math.min(total, visible + step))}
      className="btn-ghost w-full text-sm mt-3"
      data-testid={testid || "show-more"}
    >
      Show more ({total - visible} hidden)
    </button>
  );
};

/** Strip HTML tags for previews / summaries. */
export const stripHtml = (html) => {
  if (!html) return "";
  if (typeof html !== "string") return String(html);
  if (!/[<&]/.test(html)) return html; // not HTML, return as-is
  return html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
};

/** Render rich-text HTML produced by RichEditor. Use on detail views only. */
export const RichHtml = ({ html, className = "" }) => {
  if (!html) return null;
  return <div className={`rich-html ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
};
